<script>
import Vue from 'vue';
import { each } from 'lodash-es';
import {mapState, mapActions} from 'vuex';

export default Vue.extend({
    data() {
        return {
            toggled: false,
            maximized: false,
            element: undefined,
            interval: undefined,
            textFocus: false,
        }
    },
    computed: {
        ...mapState({
            loading: state => state.contract.contract.note.loading,
            emailLoading: state => state.contract.contract.email.loading,
            error: state => state.contract.contract.note.error,
            noContent: state => state.contract.contract.notes.noContent,
            notes: state => state.contract.contract.notes.item,
            user: state => state.auth.user,
            note: state => state.contract.contract.note.item,
            part: state => state.contract.contract.part,
            progress: state => state.contract.contract.progress.item,
            contractId: state => state.contract.contract.item.id,
            readyForSupplier: state => state.contract.contract.item.readyForSupplier
        })
    },
    mounted () {
        this.scrollToBottom();
        // Fetch net notes with 30 second interval
        this.interval = setInterval(() => {
            this.getContractNotes({contractId: this.contractId});
        }, 30000)
    },
    destroyed () {
        if (this.interval) {
            clearInterval(this.interval)
        }
    },
    methods: {
        ...mapActions({
            contractNoteSet: 'contract/contractNoteSet',
            createContractNote: 'contract/createContractNote',
            getContractNotes: 'contract/getContractNotes',
            setContractFormPart: 'contract/setContractFormPart',
            sendContractEmailNotification: 'contract/sendContractEmailNotification'
        }),
        sendNote () {
            this.createContractNote().then(() => {
                this.scrollToBottom();
                // If the user is a supplier then notify about sending email notification to contract manager
                if (this.user && !this.user.contractManager) {
                    if (this.notes && this.notes[this.notes.length - 1].writtenBy === this.user.id) {
                        if (confirm(this.$t('contract.form.notes.sendEmailToContractManager'))) {
                            this.sendContractEmailNotification();
                        }
                    }
                }
            })
        },
        scrollToBottom () {
            const element = document.getElementById('contract-notes__messages');
            element.scrollTop = element.scrollHeight
        },
        getProgressPartByFieldPosition (field) {
            let index = 1;
            let part = '';
            each(this.progress, (value, key) => {
                if (index === field) {
                    part = key;
                }
                ++index;
            });
            return part;
        },
        messageWithinHour (message, previousMessage) {
            let messageDate = this.$moment(message.createdAt);
            let previousMessageDate = this.$moment(previousMessage.createdAt);
            let difference = messageDate.diff(previousMessageDate, 'minutes');
            return difference > 60;
        },
        sendEmail () {
            if (confirm(this.$t('contractNotes.messages.sendEmail'))) {
                this.sendContractEmailNotification()
            }
        },
        sendTo () {
            if (this.user.contractManager) {
                if (this.readyForSupplier) {
                    return 'contractNotes.messages.supplierEmail'
                } else {
                    return 'contractNotes.messages.backOfficeEmail'
                }
            } else {
                return 'contractNotes.messages.contractManagerEmail'
            }
        }
    }
});
</script>
<template>
    <div :class="{'contract-notes--maximized' : maximized}" class="contract-notes">
        <div :class="[
            {'contract-notes__content--toggled' : toggled},
            {'contract-notes__content--maximized' : maximized}
        ]" class="contract-notes__content">
            <div class="contract-notes__header">
                <span class="contract-notes__heading" @click="toggled = !toggled">
                    {{ $t('contractNotes.header.heading') }}
                </span>
                <span v-if="notes && notes[notes.length - 1].writtenBy !== user.id"
                      :title="$t('contractNotes.header.messages')"
                      class="contract-notes__header-msg">
                    <i class="fa fa-envelope"/>
                </span>
                <span v-if="!maximized" class="contract-notes__header-maximize" @click="maximized = true">
                    <i class="far fa-window-maximize"/>
                </span>
                <span v-if="maximized" class="contract-notes__header-maximize" @click="maximized = false">
                    <i class="far fa-window-minimize"/>
                </span>
                <span :title="$t(sendTo())" class="contract-notes__header-email">
                    <i v-if="!emailLoading" class="far fa-bell" @click="sendEmail"/>
                    <i v-if="emailLoading" class="fa fa-bell"/>
                </span>
            </div>
            <div id="contract-notes__messages"
                 :class="{'contract-notes__messages--maximized' : maximized}"
                 class="contract-notes__messages">
                <div v-if="noContent" class="contract-notes__no-content">
                    {{ $t('contractNotes.messages.noContent') }}
                </div>
                <div v-for="(message, index) in notes"
                     :title="message.internal ? $t('contractNotes.messages.internal') : ''"
                     :key="'message_' + index"
                     class="contract-notes__message">
                    <div v-if="index === 0 || messageWithinHour(message, notes[index - 1])"
                         class="contract-notes__message-date">
                        {{ message.createdAt| moment('calendar') }}
                    </div>
                    <div :class="[
                             {'contract-notes__message-content--own' : user.id === message.writtenBy},
                             {'contract-notes__message-content--internal' : message.internal}
                         ]"
                         class="contract-notes__message-content" >
                        <div class="contract-notes__message-author">
                            <span v-if="message.username">
                                {{ message.username }}
                            </span>
                            <span v-if="!message.username && message.supplier">
                                {{ $t('contractNotes.messages.supplier') }}
                            </span>
                            <span v-if="!message.username && !message.supplier">
                                {{ $t('contractNotes.messages.campingVision') }}
                            </span>
                            <span>
                                {{ $t('contractNotes.messages.wrote') }}:
                            </span>
                        </div>
                        <div class="contract-notes__message-body">{{ message.note }}</div>
                        <div class="contract-notes__message-ref"
                             @click="setContractFormPart({part: getProgressPartByFieldPosition(message.field)})">
                            {{ $t('contractNotes.messages.goTo') }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="contract-notes__control">
                <div v-if="user.contractManager" class="contract-notes__set-internal">
                    <span>{{ $t('contractNotes.control.internal') }}</span>
                    <span :class="{'contract-notes__checkbox--checked' : note.internal}"
                          class="contract-notes__checkbox"
                          @click="contractNoteSet({internal: !note.internal ? 1 : 0})"/>
                </div>
                <div v-if="!user.contractManager" class="contract-notes__spacer"/>
                <div class="contract-notes__reference">
                    <span>{{ $t('contractNotes.control.reference') }}:</span>
                    <span class="contract-notes__dropdown">
                        <select @change="contractNoteSet({field: parseInt($event.target.value) + 1})">
                            <option v-for="(value, key, index) in progress"
                                    :value="index"
                                    :key="'dropdown_' + index">
                                {{ $t('contract.form.progress.part.' + key) }}
                            </option>
                        </select>
                    </span>
                </div>
                <div class="contract-notes__input">
                    <textarea :placeholder="$t('contractNotes.control.placeholder')"
                              :value="note.note"
                              :class="[
                                  {'contract-notes__input-field--focus' : textFocus},
                                  {'contract-notes__input-field--maximized-focus' : textFocus && maximized}
                              ]"
                              rows="1"
                              cols="1"
                              class="contract-notes__input-field"
                              @focus="textFocus = true"
                              @blur="textFocus = false"
                              @input="contractNoteSet({note: $event.target.value})"/>
                </div>
                <div class="contract-notes__buttons">
                    <button :disabled="loading" class="contract-notes__send-btn" @click="sendNote">
                        <i class="fa fa-fw fa-paper-plane"/>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/ContractNotes/ContractNotes.vue